import React, { memo, useMemo } from 'react';

import { Form, FormInstance } from 'antd';
import { RangePickerDateProps } from 'antd/es/date-picker/generatePicker';
import { RangePickerProps } from 'antd/lib/date-picker';
import { entries, first, omit } from 'lodash';
import moment, { Moment } from 'moment';

import { Translations } from '../../filter-bar-2';
import { SelectableList, RangeCalender } from 'components/index';
import { isMomentRange } from '../internal/type-check/is-moment-range';
import { KeyRangeValue, MomentRangeValue, PresetKeyRanges } from '../../types';
import { parseKeywordToDateRange, isPairOfDateRangeKeywords, getSafeDateRange } from './utils';

interface Props {
	name?: string;
	onFilterChange: () => void;
	datePickerProps?: Omit<RangePickerDateProps<Moment>, 'ranges' | 'value' | 'onChange'> & {
		ranges?: PresetKeyRanges;
	};
	translations: Translations;
	form: FormInstance;
}

type RangeCalenderAdapterProps = RangePickerProps & {
	status?: '' | 'warning' | 'error';
	dropdownClassName?: string;
	popupClassName?: string;
};

type FilterContentDateRangeV2RangeValue = KeyRangeValue | MomentRangeValue;

const parseRangeValue = (value: [string, string] | [Moment, Moment] | null) => {
	// Preserve the behavior when Today is shown in Range Picker inputs if 'value' is empty
	if (!value || (Array.isArray(value) && !value.length)) {
		return [moment().startOf('day'), moment().endOf('day')] as [Moment, Moment];
	}

	if (isMomentRange(value)) {
		return value;
	}

	if (isPairOfDateRangeKeywords(value)) {
		return parseKeywordToDateRange(first(value));
	}

	return value.map(stringDate => moment(stringDate)) as [Moment, Moment];
};

const RangeCalendarAdapter: React.FC<RangeCalenderAdapterProps> = memo(
	({ value, popupClassName, ...props }: RangeCalenderAdapterProps) => {
		const rangeCalendarValue = useMemo(() => parseRangeValue(value), [value]);

		return <RangeCalender {...props} popupClassName={popupClassName} value={rangeCalendarValue} />;
	}
);

export const DateRangeV2 = ({ name, onFilterChange, datePickerProps = {}, translations, form }: Props) => {
	const pickerProps = omit(datePickerProps, ['ranges']);
	const handleQuickSelection = (range: FilterContentDateRangeV2RangeValue) => {
		form.setFieldsValue({ [name]: range });
		onFilterChange();
	};

	const { ranges } = datePickerProps;

	const items = useMemo(() => {
		return entries(ranges).map(([keyword, { translatedLabel }]) => ({
			translatedLabel,
			keyword
		}));
	}, [ranges]);

	return (
		<>
			<div className="range-picker-sub-title">{translations.specifiedPeriod}</div>
			<Form.Item name={name}>
				<RangeCalendarAdapter onChange={onFilterChange} {...pickerProps} />
			</Form.Item>
			{items.length ? (
				<>
					<div className="range-picker-sub-title range-picker-title-border-top">{translations.preset}</div>
					<SelectableList
						className="quick-ranges-list"
						itemClassName="quick-range-item"
						items={items}
						onSelect={({ keyword } = {}) => handleQuickSelection([keyword, keyword])}
						renderValue={({ translatedLabel, keyword } = {}) => (
							<span data-test-id={keyword}>{translatedLabel}</span>
						)}
					/>
				</>
			) : null}
		</>
	);
};

DateRangeV2.isPairOfDateRangeKeywords = isPairOfDateRangeKeywords;
DateRangeV2.parseKeywordToDateRange = parseKeywordToDateRange;
DateRangeV2.getSafeDateRange = getSafeDateRange;
